import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button } from '@nbc-design/button';
import { Card } from '@nbc-design/card';
import { Combo, Props as ComboProps } from '@nbc-design/combo';
import { Fieldset } from '@nbc-design/fieldset';
import { Radio } from '@nbc-design/radio';
import { Text } from '@nbc-design/text';
import { InjectedIntlProps } from 'react-intl';
import { History } from 'history';
import { getTextFactory } from 'utils/TextUtils';
import { AttentionOutlineThinColor } from '@nbc-design/icons/lib/web/AttentionOutlineThinColor';
import { sendClickAnalyticsEvent } from 'services/analytics';
import { ROUTE } from 'utils/constants';
import { getBncId, saveItem } from 'utils';
import { redirectToLogin } from '../../Welcome/Welcome';
import getLogger from 'utils/getLogger';
import { signinSSO, signupSSO } from 'services/loginManager';
import { sendStepLoadedAnalyticsEvent } from '../utils';

export type ExistedApplicationProps = InjectedIntlProps & {
  history: History;
};

const ExistedApplication: FunctionComponent<ExistedApplicationProps> = ({ intl, history }) => {
  const [isClicked, setIsClicked] = useState<boolean>(false);
  const [choice, setChoice] = useState<string>('');

  useEffect(() => {
    const sendAnalytics = () => {
      const bncId = getBncId();
      sendStepLoadedAnalyticsEvent('EXISTS_APPLICATION', bncId);
    };

    sendAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getText = getTextFactory(intl, 'createCredentials');

  const logger = getLogger('Welcome');

  const manageRedirect = () => {
    setIsClicked(true);

    if (!choice) {
      return;
    }

    // Clear saved user data when starting a new application
    if (choice === 'createNewApplication') {
      const locale = localStorage.getItem('locale');
      localStorage.clear();
      sessionStorage.clear();
      if (locale) {
        localStorage.setItem('locale', locale);
      }
    }

    const authenticationOperation = choice === 'completCurrentApplication' ? signinSSO : signupSSO;
    return authenticationOperation()
      .then((response) => {
        console.log('RESPONSE: ', response);
        saveItem('newRequest', 'true');
        logger.log(`[AUTH] redirecting to STEPPER_MANAGER page`);
        window.location.assign(`${process.env.PUBLIC_URL}${ROUTE.STEPPER_MANAGER}`);
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCKS !== 'false') {
          // In local dev with mocks, skip SSO and proceed directly to stepper
          // If new application, clear server-side session to reset form data
          const resetPromise =
            choice === 'createNewApplication'
              ? fetch('/bao-tu/dev/reset-session', { method: 'POST' }).catch(() => {})
              : Promise.resolve();
          resetPromise.then(() => {
            saveItem('newRequest', 'true');
            window.location.assign(`${process.env.PUBLIC_URL}${ROUTE.STEPPER_MANAGER}`);
          });
          return;
        }
        console.log('ERROR: ', err);
        logger.error(`[AUTH] ERROR redirecting, redirecting to CLIENT_REDIRECT page`, err);
        redirectToLogin(history);
      });
  };

  const renderOptions = (): ComboProps['items'] => [
    {
      children: (
        <Radio
          id="choix1"
          name="choice"
          value="completCurrentApplication"
          label={getText('existedApplicationExplanation1')}
          text={getText('existedApplicationExplanation2')}
          onChange={(e) => {
            setChoice(e.target.value);
            sendClickAnalyticsEvent('welcome', 'Complet former application');
          }}
        />
      ),
    },
    {
      children: (
        <Radio
          id="choix2"
          name="choice"
          value="createNewApplication"
          label={getText('existedApplicationExplanation3')}
          text={getText('existedApplicationExplanation4')}
          onChange={(e) => {
            setChoice(e.target.value);
            sendClickAnalyticsEvent('welcome', 'Start new application');
          }}
        />
      ),
    },
  ];

  return (
    <Card border="shadow" className="spc-creationProfil__card mb-4">
      <div className="spc-creationProfil__popupCardBody">
        <AttentionOutlineThinColor title="attention-outline-thin-color" size={{ width: 64, height: 64 }} />
        <div className="text-wrapper">
          <Text className="mb-4 popup-title">{getText('existedApplicationTitle')}</Text>
          <Text className="application-question">{getText('existedApplicationQuestion')}</Text>
          <Fieldset validate={{ hasError: isClicked && !choice, errorMsg: getText('existedApplicationError') }}>
            <div className="radio-inline">
              <Combo id="existed application" type="form" items={renderOptions()} />
            </div>
          </Fieldset>
        </div>
        <div className="button-wrapper">
          <Button
            appearance="primary"
            onClick={() => {
              sendClickAnalyticsEvent('already existed application', 'Manage application');
              manageRedirect();
            }}
          >
            {getText('existedApplicationButton')}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ExistedApplication;
